import React, { useState, useRef, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import '../../css/LeaveQuota.css';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight, faSearch, faTimes, faChevronUp, faChevronDown, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

function parseBackendData(response, t, language) {
    const fieldMapping = {
        "CNAME": "Name",
        "KTTEXT": "Quota",
        "ENTITLE": "Entitlement",
        "DEDUCT": "Used",
        "REDUCED": "CompensatedToKeyDate",
        "REST2": "TotalRemaining",
        "BEGDA": "StartDate",
        "ENDDA": "EndDate",
        "DESTA": "DedFrom",
        "DEEND": "DedTo"
    };

    const headers = {};
    const parsedData = [];

    // Group data by LineIndex to create rows for each employee
    const rows = response?.reduce((acc, item) => {
        item.toFieldData.results.forEach((fieldData) => {
            const lineIndex = fieldData.LineIndex;
            if (!acc[lineIndex]) {
                acc[lineIndex] = {};
            }

            const columnId = item.ColumnId;
            let fieldValue = fieldData.Value.trim();
            let measureValue = t(fieldData.Measure);
            const measure = fieldData.Measure ? ` ${measureValue}` : "";

            if (fieldMapping[columnId]) {
                // Set dynamic headers
                headers[fieldMapping[columnId]] = item.ColumnHeader;

                // Format the number and remove extra decimals if needed
                if (measure && !isNaN(fieldValue)) {
                    fieldValue = parseFloat(fieldValue).toString(); // Remove unnecessary decimals
                }

                // Check if the field is a date field and format it
                if (["StartDate", "EndDate", "DedFrom", "DedTo"].includes(fieldMapping[columnId])) {
                    acc[lineIndex][fieldMapping[columnId]] = formatDate(fieldValue, language);
                } else {
                    acc[lineIndex][fieldMapping[columnId]] = fieldValue + measure;
                }
            }
        });

        return acc;
    }, {});

    Object.values(rows).forEach(row => parsedData.push(row));

    return { headers, data: parsedData };
}

// Helper function to format date from YYYYMMDD to DD-MM-YYYY
const formatDate = (dateStr, language) => {
    if (!dateStr) return "";
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6) - 1;
    const day = dateStr.slice(6, 8);
    const date = new Date(year, month, day);

    return date.toLocaleDateString(language, { month: 'long', day: 'numeric', year: 'numeric' });
};

const EmployeesLeavesBalances = ({ data: backendData, language, isDisabled, onSendMessage }) => {
    const { t } = useTranslation();
    sessionStorage.setItem('clearHistory', true);
    useEffect(() => {
        const timer = setTimeout(() => {
            onSendMessage({
                sender: 'bot',
                isRating: true,
                requestType: "success",
                timestamp: new Date().getTime(),
            });
        }, (process.env.REACT_APP_TIME_TO_DISPLAY_REVIEW * 1000));

        return () => clearTimeout(timer);
    }, []);

    const [data, setData] = useState([]);
    const [headers, setHeaders] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState(backendData.employeeName || '');
    const [showSearchBox, setShowSearchBox] = useState(!!backendData.employeeName);
    const [expandedRows, setExpandedRows] = useState({});
    const searchInputRef = useRef(null);
    const [isFormDisabled, setIsFormDisabled] = useState(isDisabled);
    const itemsPerPage = 5;

    useEffect(() => {
        setIsFormDisabled(isDisabled);
    }, [isDisabled]);

    useEffect(() => {
        const parsedResponse = parseBackendData(backendData.data, t, language);
        setData(parsedResponse.data);
        setHeaders(parsedResponse.headers);
        if (backendData.employeeName) {
            setSearchTerm(backendData.employeeName);
        }
    }, [backendData]);

    const filteredData = data?.filter((leave) => {
        const searchTerms = searchTerm.toLowerCase().trim().split(' ');

        return searchTerms.every(term =>
            leave.Name?.toLowerCase().includes(term) ||
            leave.Quota?.toString().toLowerCase().includes(term)
        );
    }) || [];

    const filteredDataUnion = filteredData.length > 0 ? filteredData : data?.filter((leave) => {
        const searchTerms = searchTerm.toLowerCase().trim().split(' ');

        return searchTerms.some(term =>
            leave.Name?.toLowerCase().includes(term) ||
            leave.Quota?.toString().toLowerCase().includes(term)
        );
    }) || [];

    const offset = currentPage * itemsPerPage;
    const currentItems = filteredDataUnion.slice(offset, offset + itemsPerPage);

    const handlePageChange = ({ selected }) => {
        if (!isFormDisabled) {
            setCurrentPage(selected);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    const toggleSearchBox = () => {
        setShowSearchBox(!showSearchBox);
        setSearchTerm('');
        if (!showSearchBox) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    };
    useEffect(() => {
        if (showSearchBox && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showSearchBox]);

    const toggleRow = (index) => {
        setExpandedRows(prevState => ({
            [index]: !prevState[index]
        }));
    };

    return (
        <div className={`table-container table-container-leave w-100 ${isFormDisabled ? 'form-disabled' : ''}`}>
            <div className="table-header d-flex justify-content-between align-items-center">
                <div className="search-container w-100 d-flex align-items-center">
                    <input
                        type="text"
                        placeholder={t('Search by Employee Name or Quota')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-input form-control"
                        ref={searchInputRef}
                        disabled={isFormDisabled}
                    />
                </div>
            </div>
            {filteredDataUnion.length > 0 ? (
                <>
                    <table>
                        <thead>
                            <tr>
                                <th>{t(headers.Name) || t('Employee Name')}</th>
                                <th>{t(headers.Quota) || t('Quota')}</th>
                                <th>{t(headers.Entitlement) || t('Entitlement')}</th>
                                <th>{t(headers.TotalRemaining) || t('Total Remaining')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((leave, index) => (
                                <React.Fragment key={index}>
                                    <tr>
                                        <td>{leave.Name}</td>
                                        <td>{leave.Quota}</td>
                                        <td>{leave.Entitlement}</td>
                                        <td>{leave.TotalRemaining}</td>
                                        <td>
                                            <button onClick={() => toggleRow(index)} className={`btn btn-sm more-table-btn ${expandedRows[index] ? 'open' : ''}`} disabled={isFormDisabled}>
                                                <FontAwesomeIcon icon={expandedRows[index] ? faChevronUp : faChevronDown} />
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedRows[index] && (
                                        <tr>
                                            <td colSpan="5">
                                                <div className="td-container">
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.StartDate) || t('Start Date')}</div>
                                                        <div className='td-container-item-value'>{leave.StartDate}</div>
                                                    </div>
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.EndDate) || t('End Date')}</div>
                                                        <div className='td-container-item-value'>{leave.EndDate}</div>
                                                    </div>
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.DedFrom) || t('Deducted From')}</div>
                                                        <div className='td-container-item-value'>{leave.DedFrom}</div>
                                                    </div>
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.DedTo) || t('Deducted To')}</div>
                                                        <div className='td-container-item-value'>{leave.DedTo}</div>
                                                    </div>
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.Used) || t('Used')}</div>
                                                        <div className='td-container-item-value'>{leave.Used}</div>
                                                    </div>
                                                    <div className='td-container-item'>
                                                        <div className='td-container-item-title'>{t(headers.CompensatedToKeyDate) || t('Compensated to Key Date')}</div>
                                                        <div className='td-container-item-value'>{leave.CompensatedToKeyDate}</div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    {filteredDataUnion.length > itemsPerPage && (
                        <ReactPaginate
                            previousLabel={<FontAwesomeIcon icon={language === 'ar' ? faCaretRight : faCaretLeft} />}
                            nextLabel={<FontAwesomeIcon icon={language === 'ar' ? faCaretLeft : faCaretRight} />}
                            breakLabel="..."
                            pageCount={Math.ceil(filteredDataUnion.length / itemsPerPage)}
                            marginPagesDisplayed={1}
                            pageRangeDisplayed={1}
                            onPageChange={handlePageChange}
                            containerClassName="pagination"
                            pageClassName={`page-item ${isFormDisabled ? 'disabled' : ''}`}
                            pageLinkClassName="page-link"
                            previousClassName={`page-item ${isFormDisabled ? 'disabled' : ''}`}
                            previousLinkClassName="page-link"
                            nextClassName={`page-item ${isFormDisabled ? 'disabled' : ''}`}
                            nextLinkClassName="page-link"
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            activeClassName="active"
                        />
                    )}
                </>
            ) : (
                <p className='center-text'><FontAwesomeIcon className='warning-icon' icon={faTriangleExclamation} />{t("Sorry, There is no Employees")}</p>
            )}
        </div>
    );
};

export default EmployeesLeavesBalances;
